import { Injectable, inject } from '@angular/core';
import { AutocompleteForFiltersItem } from '@app/shared/forms/autocomplete-for-filters/autocomplete-for-filters-item.interface';
import { ViewOptions, ViewService } from '@makiwin/ngx-drupal8-rest';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CoachesService {
  private readonly viewService: ViewService = inject(ViewService);
  private readonly apiEndpointDefault = '/api/v1/coaches-search';
  private readonly fieldPictureDefault = 'user_picture';

  /**
   * Fetch coaches from the API for the autocomplete inputboxes, filtered and paginatead by the viewOptions parameter.
   *
   * @param viewOptions
   *   View options to filter and paginate the results.
   * @returns {Observable<AutocompleteForFiltersItem[]>}
   *   OBSERVABLE: Array of AutocompleteForFiltersItem with the result of the search.
   */
  getForAutoComplete(options: {
    viewOptions?: ViewOptions;
    apiEndpoint?: string;
    fieldPicture?: string;
  }): Observable<AutocompleteForFiltersItem[]> {
    return this.viewService
      .get(options?.apiEndpoint ?? this.apiEndpointDefault, options.viewOptions)
      .pipe(
        map((coaches) =>
          coaches.map((coach: any) => this.convertCoachToAutoCompleteForFilterItem(coach, options?.fieldPicture))
        )
      );
  }

  /**
   * Fetch a coach from the API for the autocomplete inputboxes, filtered by the id parameter.
   *
   * @param id
   *   Id of the coach to fetch.
   * @returns {Observable<AutocompleteForFiltersItem | null>}
   *   OBSERVABLE: AutocompleteForFiltersItem with the result of the search.
   */
  getByIdForAutoComplete(id: string, fieldPicture?: string): Observable<AutocompleteForFiltersItem | null> {
    return this.getForAutoComplete({
      viewOptions: {
        filters: {
          uid: id,
        },
      },
      fieldPicture,
    }).pipe(map((coaches) => coaches?.[0]));
  }

  /**
   * Convert a coach object to an AutocompleteForFiltersItem object.
   * @param coach
   * Coach object to convert.
   * @param [userPictureField]
   * Field name of the picture of the coach.
   * @returns
   * Converted coach in AutocompleteForFiltersItem object.
   */
  convertCoachToAutoCompleteForFilterItem(coach: any, userPictureField?: string): AutocompleteForFiltersItem {
    userPictureField = userPictureField ?? this.fieldPictureDefault;
    return {
      id: coach.uid,
      label: `${coach.field_first_name} ${coach.field_surname}`,
      objectValues: coach,
      picture: coach[userPictureField]?.square_md,
    };
  }
}
